import React, { useRef, useEffect } from 'react';

const InteractiveParticles = ({ config }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let particles = [];
    
    // Detectar móvil
    const isMobile = window.innerWidth < 768;

    // Offset base para la barra de navegación en móviles
    const bottomOffset = isMobile ? 80 : 0; 

    const cursor = { 
        x: -1000, 
        y: -1000, 
        radius: isMobile ? 120 : 100 
    };

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', resize);
    resize();

    // --- MANEJO DE EVENTOS ---
    const handleMouseMove = (e) => { cursor.x = e.clientX; cursor.y = e.clientY; };
    const handleMouseOut = () => { cursor.x = -1000; cursor.y = -1000; };
    
    const handleTouchMove = (e) => {
        if(e.touches.length > 0) {
            cursor.x = e.touches[0].clientX;
            cursor.y = e.touches[0].clientY;
        }
    };
    const handleTouchEnd = () => { cursor.x = -1000; cursor.y = -1000; };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseout', handleMouseOut);
    window.addEventListener('touchstart', handleTouchMove, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd);


    // --- CLASE PARTÍCULA ---
    class Particle {
      constructor() {
        this.init(true); 
      }

      init(randomY = false) {
        const minSize = isMobile ? 15 : 20;
        const sizeVariation = isMobile ? 15 : 25;

        this.size = Math.random() * sizeVariation + minSize; 
        this.image = config.emojis[Math.floor(Math.random() * config.emojis.length)];
        
        this.x = Math.random() * canvas.width;
        // Inicio: si es la primera vez (randomY) aparecen distribuidas, sino caen de arriba
        this.y = randomY ? Math.random() * canvas.height : -this.size - Math.random() * 200;
        
        this.vx = (Math.random() - 0.5) * 1; 
        this.vy = Math.random() * 1 + 1;     
        
        this.angle = Math.random() * Math.PI * 2;
        this.spin = (Math.random() - 0.5) * 0.03;
        
        this.isResting = false;

        // --- CORRECCIÓN: Suelo Irregular Extremo ---
        // Math.random() da 0 a 1.
        // Restamos 0.3 para que algunas (30%) caigan un poco "bajo tierra" (ocultas parcialmente).
        // Multiplicamos por 200 para que las que quedan arriba formen montañas altas.
        // Resultado: Variación de -60px (abajo) a +140px (arriba).
        this.floorVariance = (Math.random() - 0.3) * 200; 
      }

      draw() {
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.angle);
        ctx.font = `${this.size}px serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(this.image, 0, 0);
        ctx.restore();
      }

      update() {
        // 1. INTERACCIÓN
        const dx = cursor.x - this.x;
        const dy = cursor.y - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < cursor.radius) {
            const forceDirectionX = dx / distance;
            const forceDirectionY = dy / distance;
            const force = (cursor.radius - distance) / cursor.radius;
            
            const pushPower = isMobile ? 12 : 8; 
            
            this.vx -= forceDirectionX * force * pushPower;
            this.vy -= forceDirectionY * force * pushPower;
            
            if (this.isResting) {
                this.isResting = false;
                this.vy -= 5; 
            }
        }

        // 2. MOVIMIENTO
        if (!this.isResting) {
            this.x += this.vx;
            this.y += this.vy;
            this.angle += this.spin;

            this.vy += 0.05; // Gravedad
            this.vx *= 0.98; 
            this.vy *= 0.98;

            // 3. DETECCIÓN DEL SUELO (IRREGULAR)
            // El suelo base es la altura del canvas menos el offset móvil.
            // Le restamos la varianza: si floorVariance es positivo, el suelo sube (montaña).
            // Si es negativo, el suelo baja (hueco).
            const floorLevel = canvas.height - this.size - bottomOffset - this.floorVariance;

            if (this.y > floorLevel) {
                this.y = floorLevel;
                this.vy *= -0.4; // Rebote
                this.vx *= 0.8; 

                if (Math.abs(this.vy) < 0.5 && Math.abs(this.vx) < 0.5) {
                    this.isResting = true;
                }
            }
        }

        // 4. REINICIO
        if (!this.isResting) {
            const margin = 150; // Margen más amplio para que no desaparezcan al rebotar alto
            
            // Reiniciamos si salen por los lados o por arriba
            // NO reiniciamos si salen por abajo (porque la lógica de suelo irregular puede permitirlo)
            if (this.x < -margin || this.x > canvas.width + margin || this.y < -margin) {
                 this.init(false);
            }
            
            // Seguridad extra: Si por alguna razón extrema caen muuuuy abajo (más allá de la varianza)
            if (this.y > canvas.height + 200) {
                this.init(false);
            }
        }
      }
    }

    const init = () => {
      particles = [];
      // Aumentamos ligeramente la cantidad para que el efecto de "montón" se note más
      const amount = isMobile ? 15 : 25; 
      for (let i = 0; i < amount; i++) {
        particles.push(new Particle());
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.update();
        p.draw();
      });
      animationFrameId = requestAnimationFrame(animate);
    };

    init();
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseout', handleMouseOut);
      
      window.removeEventListener('touchstart', handleTouchMove);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      
      cancelAnimationFrame(animationFrameId);
    };
  }, [config]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed', 
        top: 0, 
        left: 0, 
        width: '100%', 
        height: '100%', 
        pointerEvents: 'none', 
        zIndex: 9999
      }}
    />
  );
};

export default InteractiveParticles;